import React, { useMemo, useState, useEffect, useRef } from "react";
import { Box, Typography, Avatar, Button } from "@mui/material";
import TablePagination from "@mui/material/TablePagination";
import { StatusDot, getActionButtonStyles } from "../../../utils/index";
import {
  chracterPosition,
  gridTemplateColumnsResponsive,
} from "../../../utils/index";
import { colorCodesForAvatar } from "../../../constants/index";
import { useDispatch, useSelector } from "react-redux";
import { getUserTrafficList, upsertVisitor, resetUserTrafficState, toUserTrafficErrorMessage } from "../../../store/features/getUserTrafficList";
import { msgTimeWithSeconds } from "../../../utils/index";
import { useSocket } from "../../../SocketContext";
import TableLoadingSkeleton from "../Traffic/TrafficTableSkeleton";
import { useNavigate, useSearchParams } from "react-router-dom";
import { assigningRoom } from "../../../store/features/assigningRoom";
import { toast } from "react-toastify";
import {
  rememberInvitedVisitor,
  shouldAnnounceInvitedVisitorJoin,
} from "../../../utils/invitedChats";
import { closeTrafficUserDetailSidebar } from "../../../store/features/trafficUserDetailSidebarSlice";
import NoDataFound from "../Traffic/NoDataFound";
import {
  joinGroupTrafficRoom,
  leaveGroupTrafficRoom,
} from "../../../utils/groupTrafficSocketRoom";
import { LIVE_CHAT_HOME_PATH } from "../../../config/featureFlags";

const UserTrafficTable = ({
  activeTab = 0,
  searchQuery = "",
  onUserSelect,
  counts,
  selectedGroupId = null,
}) => {
  const dispatch = useDispatch();
  const { loading, error, data, pagination } = useSelector((s) => s.userTraffic);
  const socket = useSocket();
  const userData = useSelector((state) => state.loginToken);
  const coUserData = useSelector((state) => state.loginTokenCoUser);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const businessId =
    userData?.response?.user?.business_id ||
    coUserData?.response?.user?.business_id ||
    null;
  const agentId =
    userData?.response?.user?._id || coUserData?.response?.user?._id || null;

  const [selectedRowId, setSelectedRowId] = useState(null);

  // Read inside socket handlers, which are bound once per group.
  const visitorsRef = useRef(data);
  useEffect(() => {
    visitorsRef.current = data;
  }, [data]);
  useEffect(() => {
    if (!socket) return;

    // Remove previous listener and attach once
    socket.off("invite_error");
    socket.on("invite_error", (error) => {
      toast.error(error?.message || "You are not accepting chat");
    });

    return () => {
      socket.off("invite_error");
    };
  }, [socket]);

  const [page, setPage] = useState(0); // zero-based for UI
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const groupIdRef = useRef(selectedGroupId);

  useEffect(() => {
    if (!selectedGroupId) return;

    const previousGroupId = groupIdRef.current;
    const groupChanged =
      previousGroupId != null && previousGroupId !== selectedGroupId;
    groupIdRef.current = selectedGroupId;

    if (groupChanged) {
      dispatch(resetUserTrafficState());
      if (page !== 0) {
        setPage(0);
        return;
      }
    }

    dispatch(
      getUserTrafficList({
        page: page + 1,
        limit: rowsPerPage,
        groupId: selectedGroupId,
      })
    );
  }, [dispatch, page, rowsPerPage, selectedGroupId]);

  useEffect(() => {
    if (!socket || !businessId || !selectedGroupId) return;

    const onVisitorInfo = (payload) => {
      try {
        if (Array.isArray(payload)) {
          const filteredPayload = payload.filter(
            (v) => (v?.status || "").toLowerCase() !== "left"
          );
          filteredPayload.forEach((v) => dispatch(upsertVisitor(v)));
        } else if (payload && typeof payload === "object") {
          if ((payload?.status || "").toLowerCase() !== "left") {
            dispatch(upsertVisitor(payload));
          }
        }
      } catch (_) { }
    };

    const onVisitorStatusUpdated = ({
      visitorId,
      status,
      last_seen,
      currentRoom,
      name,
      email,
      ip_address,
    }) => {
      try {
        if (!visitorId) return;
        dispatch(
          upsertVisitor({
            _id: visitorId,
            status,
            last_seen,
            currentRoom,
            name,
            email,
            ip_address,
          })
        );

        // The awaited answer to an invite sent from this table. `msg fetched`
        // is not a dependable trigger for it, but this status update is.
        if (shouldAnnounceInvitedVisitorJoin(visitorId, { status, currentRoom })) {
          const stored = (visitorsRef.current || []).find(
            (v) => (v?._id || v?.id) === visitorId
          );
          const label = String(name || stored?.name || "").trim() || "A visitor";
          toast.info(`${label} has joined the chat`, {
            toastId: `visitor-joined-${visitorId}`,
            onClick: currentRoom
              ? () => navigate(`${LIVE_CHAT_HOME_PATH}?roomId=${currentRoom}`)
              : undefined,
          });
        }

        if (status === "left") {
          dispatch(closeTrafficUserDetailSidebar());
          const params = new URLSearchParams(searchParams.toString());
          params.delete("userId");
          setSearchParams(params, { replace: false });
        }
      } catch (_) { }
    };

    const rejoinGroupTraffic = () => {
      joinGroupTrafficRoom(socket, businessId, selectedGroupId);
    };

    rejoinGroupTraffic();
    socket.on("connect", rejoinGroupTraffic);
    socket.on("visitor_info", onVisitorInfo);
    socket.on("visitor_status_updated", onVisitorStatusUpdated);

    return () => {
      leaveGroupTrafficRoom(socket, businessId, selectedGroupId);
      socket.off("connect", rejoinGroupTraffic);
      socket.off("visitor_info", onVisitorInfo);
      socket.off("visitor_status_updated", onVisitorStatusUpdated);
    };
  }, [socket, businessId, selectedGroupId, dispatch, searchParams, setSearchParams]);

  const statusByTabIndex = {
    1: "browsing",
    2: "queued",
    3: "chatting",
    5: "invited",
  };

  const normalizedQuery = searchQuery.trim().toLowerCase();

  const mapStatusToAction = (status) => {
    switch ((status || "").toLowerCase()) {
      case "chatting":
        return { action: "View Chat", actionType: "blue" };
      case "queued":
        return { action: "Pick from Queue", actionType: "green" };
      case "invited":
        return { action: "View Chat", actionType: "blue" };
      case "browsing":
      default:
        return { action: "Invite to Chat", actionType: "white" };
    }
  };

  const [timeTick, setTimeTick] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setTimeTick((prev) => prev + 1);
    }, 10000);
    return () => clearInterval(timer);
  }, []);

  const normalizedUsers = useMemo(() => {
    if (!Array.isArray(data)) return [];
    const filteredData = data.filter(
      (item) => (item?.status || "").toLowerCase() !== "left"
    );

    console.log(
      filteredData,
      "dataaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaa"
    );
    return filteredData.map((item) => {
      const status = item?.status || "browsing";
      const name = item?.name || "Unknown";
      const email = item?.email || "-";
      const lastSeen = item?.last_seen || item?.updatedAt || item?.createdAt;
      const time = getTimeDiffString(lastSeen);
      const { action, actionType } = mapStatusToAction(status);
      return {
        ...item,
        id: item?._id || item?.id,
        name,
        email,
        activity: status.charAt(0).toUpperCase() + status.slice(1),
        time,
        action,
        actionType,
        trafficStatus: status,
        local_time: item?.local_time,
        chatWith:
          item?.currentRoom?.assigned_to?.full_name ||
          item?.current_room?.assigned_to?.full_name,
        currentRoom: item?.currentRoom?._id || item?.current_room,
        ipAddress: item?.ip_address,
      };
    });
  }, [data, timeTick]);

  const filteredUsers = useMemo(() => {
    return normalizedUsers
      .filter((u) => {
        if (activeTab === 0) return true;
        const status = statusByTabIndex[activeTab];
        return (u.trafficStatus || "browsing") === status;
      })
      .filter((u) => {
        if (!normalizedQuery) return true;
        return (
          (u.name || "").toLowerCase().includes(normalizedQuery) ||
          (u.email || "").toLowerCase().includes(normalizedQuery)
        );
      });
  }, [activeTab, normalizedQuery, normalizedUsers]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleRowClick = (user) => {
    setSelectedRowId(user.id || user._id);
    if (typeof onUserSelect === "function") onUserSelect(user);
  };

  const redirectToChat = (user) => {
    const visitorId = user?.currentRoom;
    if (!visitorId) return;
    navigate(`${LIVE_CHAT_HOME_PATH}?roomId=${visitorId}`);
  };

  const handleActionClick = async (event, user) => {
    event.stopPropagation();
    if (!socket) return;

    console.log(user, "user");

    // --- View Chat ---
    if (
      user?.actionType === "blue" &&
      (user?.action || "").toLowerCase() === "view chat"
    ) {
      redirectToChat(user);
      return;
    }

    // --- Pick from Queue ---
    if (
      user?.actionType === "green" &&
      (user?.action || "").toLowerCase() === "pick from queue"
    ) {
      const visitorId = user?.currentRoom || user?._id || user?.id;

      const response = await dispatch(
        assigningRoom({
          requestHeaders: {
            mainUserId: userData?.response?.user._id,
            roomId: user?.currentRoom,
          },
        })
      );

      console.log(response, "response");

      if (response?.payload?.success === false) {
        toast.error(response?.payload?.message || "You are not accepting chat");
        return;
      }

      if (visitorId) {
        navigate(`${LIVE_CHAT_HOME_PATH}?roomId=${visitorId}`);
      }
      return;
    }

    // --- Invite to Chat ---
    const isInviteAction =
      user?.actionType === "white" &&
      (user?.action || "").toLowerCase() === "invite to chat";

    if (!isInviteAction) return;

    const visitorId = user?._id || user?.id;
    if (!visitorId || !businessId || !agentId) return;

    // Only emit here, don't attach listeners multiple times
    socket.emit("invite_user", {
      visitorId,
      businessId,
      agentId,
    });

    // Lets the app toast when this visitor actually joins the chat.
    rememberInvitedVisitor(visitorId);
  };

  // With server-side pagination we render the current page items directly
  const visibleUsers = filteredUsers;

  const listContainerHeight = useMemo(() => `calc(100vh - 370px)`, []);

  if (!selectedGroupId || (loading && data.length === 0)) {
    return <TableLoadingSkeleton />;
  }

  if (visibleUsers.length === 0 && !loading && !error) {
    return <NoDataFound />;
  }
  function getTimeDiffString(fromDate, toDate = new Date()) {
    const from = new Date(fromDate);
    const to = new Date(toDate);

    const diffMs = Math.abs(to - from);

    const totalSeconds = Math.floor(diffMs / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    if (hours === 0 && minutes === 0 && seconds === 0) return "just now";
    if (hours === 0 && minutes === 0) return `${seconds}s`;
    if (hours === 0) return `${minutes}min ${seconds}s`;
    if (minutes === 0 && seconds === 0) return `${hours}h`;
    if (seconds === 0) return `${hours}h ${minutes}min`;
    return `${hours}h ${minutes}min ${seconds}s`;
  }


  return (
    <Box sx={{ marginTop: "53px" }}>
      <Box
        sx={{
          width: "100%",
        }}
      >
        <Box
          sx={{
            height: listContainerHeight,
            overflowY: "auto",
            boxShadow: "none",
            marginBottom: "40px",
          }}
        >
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: gridTemplateColumnsResponsive,
              gap: "16px",
              paddingBottom: "9px",
              position: "sticky",
              top: 0,
              zIndex: 2,
              background: "#fff",
              borderBottom: "1px solid #E5E9EC",
            }}
          >
            <Typography
              sx={{
                color: "#405261",
                fontFamily: "Inter",
                fontSize: "12px",
                fontWeight: 400,
                lineHeight: "18px",
              }}
            >
              Name
            </Typography>
            <Typography
              sx={{
                color: "#405261",
                fontFamily: "Inter",
                fontSize: "12px",
                fontWeight: 400,
                lineHeight: "18px",
              }}
            >
              Email
            </Typography>
            <Typography
              sx={{
                color: "#405261",
                fontFamily: "Inter",
                fontSize: "12px",
                fontWeight: 400,
                lineHeight: "18px",
              }}
            >
              Actions
            </Typography>
            <Typography
              sx={{
                color: "#405261",
                fontFamily: "Inter",
                fontSize: "12px",
                fontWeight: 400,
                lineHeight: "18px",
              }}
            >
              Activity
            </Typography>
            <Typography
              sx={{
                color: "#405261",
                fontFamily: "Inter",
                fontSize: "12px",
                fontWeight: 400,
                lineHeight: "18px",
              }}
            >
              Time
            </Typography>
            <Typography
              sx={{
                color: "#405261",
                fontFamily: "Inter",
                fontSize: "12px",
                fontWeight: 400,
                lineHeight: "18px",
              }}
            >
              Chat With
            </Typography>

          </Box>

          {error && !loading && (
            <Box sx={{ padding: 2 }}>
              <Typography sx={{ color: "#d32f2f", fontFamily: "Inter" }}>
                {toUserTrafficErrorMessage(error)}
              </Typography>
            </Box>
          )}

          {!loading &&
            !error &&
            visibleUsers.map((user) => (
              <Box key={user.id || user._id}>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: gridTemplateColumnsResponsive,
                    gap: "16px",
                    alignItems: "center",
                    paddingY: "16px",
                    cursor: "pointer",
                    "&:hover": { backgroundColor: "#f9fafa" },
                    backgroundColor:
                      (user.id || user._id) === selectedRowId
                        ? "#F9FAFA"
                        : "transparent",
                  }}
                  onClick={() => handleRowClick(user)}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: "12px",
                      minWidth: 0,
                      overflow: "hidden",
                    }}
                  >
                    {user?.avatar ? (
                      <Avatar
                        src={user.avatar}
                        sx={{ width: 32, height: 32, flexShrink: 0 }}
                      />
                    ) : (
                      <Avatar
                        sx={{
                          width: 32,
                          height: 32,
                          flexShrink: 0,
                          fontSize: "16px",
                          fontWeight: "500",
                          bgcolor:
                            colorCodesForAvatar[
                            chracterPosition(
                              user?.name?.substring(0, 1)?.toUpperCase()
                            )
                            ],
                        }}
                      >
                        {user?.name?.charAt(0).toUpperCase()}
                      </Avatar>
                    )}

                    <Typography
                      component="span"
                      title={
                        user.name.includes("Visitor")
                          ? (user.ipAddress || "IP not available").trim()
                          : user.name.charAt(0).toUpperCase() + user.name.slice(1)
                      }
                      sx={{
                        color: "#061522",
                        fontFamily: "Inter",
                        fontSize: "14px",
                        fontWeight: 500,
                        lineHeight: "18px",
                        minWidth: 0,
                        flex: 1,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {user.name.includes("Visitor")
                        ? user.ipAddress || "IP not available"
                        : user.name.charAt(0).toUpperCase() + user.name.slice(1)}
                    </Typography>
                  </Box>

                  <Box
                    sx={{ display: "flex", alignItems: "center", gap: "6px" }}
                  >
                    <Typography
                      sx={{
                        color: "#405261",
                        fontFamily: "Inter",
                        fontSize: "14px",
                        fontWeight: 400,
                        lineHeight: "18px",
                        maxWidth: '200px',
                        textOverflow: 'ellipsis',
                        overflow: 'hidden',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {user.email || "-"}
                    </Typography>
                  </Box>

                  <Box sx={{ paddingRight: "40px" }}>

                    <Button
                      sx={getActionButtonStyles(user.actionType)}
                      onClick={(e) => handleActionClick(e, user)}
                    >
                      {user.action}
                    </Button>
                  </Box>

                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <StatusDot status={user.activity} />
                    <Typography
                      sx={{
                        color: "#405261",
                        fontFamily: "Inter",
                        fontSize: "14px",
                        fontWeight: 400,
                        lineHeight: "18px",
                      }}
                    >
                      {user.activity}
                    </Typography>
                  </Box>

                  <Typography
                    sx={{
                      color: "#405261",
                      fontFamily: "Inter",
                      fontSize: "14px",
                      fontWeight: 400,
                      lineHeight: "18px",
                    }}
                  >
                    {user.time}
                  </Typography>

                  <Typography
                    sx={{
                      color: "#061522",
                      fontFamily: "Inter",
                      fontSize: "14px",
                      fontWeight: 500,
                      lineHeight: "18px",
                    }}
                  >
                    {user.chatWith || "-"}
                  </Typography>


                </Box>

                <Box
                  sx={{
                    height: "1px",
                    backgroundColor: "#E5E9EC",
                    width: "100%",
                  }}
                />
              </Box>
            ))}
        </Box>
      </Box>

      {(pagination?.total ?? filteredUsers.length) >= 10 &&
        !loading &&
        !error && (
        <Box
          sx={{
            position: "sticky",
            bottom: 0,
            left: 0,
            width: "100%",
            background: "#fff",
            zIndex: 10,
            padding: "16px 24px",
            borderTop: "1px solid #E5E9EC",
          }}
        >
          <TablePagination
            component="div"
            count={pagination?.total || 0}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            rowsPerPageOptions={[]}
            onRowsPerPageChange={handleChangeRowsPerPage}
            labelDisplayedRows={({ from, to, count }) => (
              <Typography
                sx={{
                  color: "#405261",
                  fontFamily: "Inter",
                  fontSize: "14px",
                  fontStyle: "normal",
                  fontWeight: 400,
                  lineHeight: "18px",
                }}
              >
                Visitors {from}-{to}
              </Typography>
            )}
            ActionsComponent={(props) => (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  marginLeft: "auto",
                }}
              >
                <Button
                  onClick={(e) => props.onPageChange(e, props.page - 1)}
                  disabled={props.page === 0 || !pagination?.hasPrevPage}
                  sx={{
                    width: "128px",
                    height: "40px",
                    flexShrink: 0,
                    borderRadius: "8px",
                    border: "1px solid #D5DCE1",
                    background: "#FFF",
                    boxShadow: "0px 1px 2px 0px rgba(6, 21, 34, 0.05)",
                    color: "#405261",
                    fontSize: "14px",
                    fontWeight: 500,
                    lineHeight: "18px",
                    textTransform: "none",
                    "&:not(.Mui-disabled):hover": {
                      backgroundColor: "#FFFFFF",

                      borderColor: "#C5CED6",
                    },
                    "&.Mui-disabled": {
                      color: "#93A1B0",
                      border: "1px solid #C5CED6",
                      bgcolor: "#F9FAFA",
                    },
                  }}
                >
                  Previous
                </Button>
                <Button
                  onClick={(e) => props.onPageChange(e, props.page + 1)}
                  disabled={
                    pagination?.hasNextPage === false ||
                    (pagination?.totalPages ? props.page >= (pagination.totalPages - 1) : false)
                  }
                  sx={{
                    width: "88px",
                    height: "40px",
                    flexShrink: 0,
                    borderRadius: "8px",
                    border: "1px solid #D5DCE1",
                    background: "#FFF",
                    boxShadow: "0px 1px 2px 0px rgba(6, 21, 34, 0.05)",
                    color: "#405261",
                    fontSize: "14px",
                    fontWeight: 500,
                    lineHeight: "18px",
                    textTransform: "none",
                    "&:not(.Mui-disabled):hover": {
                      backgroundColor: "#FFFFFF",

                      borderColor: "#C5CED6",
                    },
                    "&.Mui-disabled": {
                      color: "#93A1B0",
                      border: "1px solid #C5CED6",
                      bgcolor: "#F9FAFA",
                    },
                  }}
                >
                  Next
                </Button>
              </Box>
            )}
            sx={{
              ".MuiTablePagination-toolbar": {
                padding: 0,
                minHeight: "unset",
                display: "flex",
                justifyContent: "space-between",
              },
              ".MuiTablePagination-spacer": {
                display: "none",
              },
              ".MuiTablePagination-selectLabel": {
                display: "none",
              },
              ".MuiTablePagination-select": {
                display: "none",
              },
              ".MuiTablePagination-selectIcon": {
                display: "none",
              },
            }}
          />
        </Box>
      )}
    </Box>
  );
};

export default UserTrafficTable;
